import React from 'react';
import { AppBar, Box, IconButton, Typography, Tooltip, useMediaQuery, useTheme, Drawer, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CommentIcon from '@mui/icons-material/Comment';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const DRAWER_WIDTH = 240;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    background: 'linear-gradient(180deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
    borderRight: '1px solid rgba(139, 92, 246, 0.1)',
  },
}));

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [moreAnchorEl, setMoreAnchorEl] = React.useState(null);

  const navItems = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: DashboardIcon,
      description: "Get a quick overview of your content performance metrics"
    },
    {
      label: "Trending",
      path: "/",
      icon: TrendingUpIcon,
      description: "Analyze trending hashtags to optimize your content reach"
    },
    {
      label: "AI Strategy",
      path: "/content-strategy",
      icon: AutoAwesomeIcon,
      description: "Get AI-powered content ideas based on trending TikTok data"
    },
    {
      label: "Comments",
      path: "/comments",
      icon: CommentIcon,
      description: "Analyze comment sentiment and engagement patterns"
    },
    {
      label: "Like Rate",
      path: "/like-rate",
      icon: FavoriteIcon,
      description: "Track your content's like-to-view ratio performance"
    },
    {
      label: "Conversion",
      path: "/conversion",
      icon: PeopleIcon,
      description: "Monitor viewer engagement and interaction rates"
    },
    {
      label: "Profile",
      path: "/profile",
      icon: PersonIcon,
      description: "Analyze your profile's overall performance metrics"
    },
  ];

  // Show only the first 4 items on mobile, rest go in "More" menu
  const visibleNavItems = isMobile ? navItems.slice(0, 4) : navItems;
  const moreNavItems = isMobile ? navItems.slice(4) : [];

  const handleMoreClick = (event) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const handleMoreItemClick = (path) => {
    navigate(path);
    handleMoreClose();
  };

  const renderNavItems = () => (
    <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'row' : 'column',
          justifyContent: isMobile ? 'space-between' : 'flex-start',
          py: isMobile ? 2 : 4,
          px: isMobile ? 2 : 2,
          gap: isMobile ? 1 : 2,
          height: '100%',
          width: '100%',
          maxWidth: isMobile ? 600 : '100%',
          mx: 'auto'
        }}
      >
        {visibleNavItems.map(({ label, path, icon: Icon, description }) => {
          const isSelected = location.pathname === path;
          return (
            <Tooltip 
              key={path}
              title={description}
              placement="top"
              enterDelay={500}
              arrow
            >
              <Box
                onClick={() => navigate(path)}
                sx={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: 'center',
                  cursor: 'pointer',
                  flex: isMobile ? 1 : 'none',
                  maxWidth: isMobile ? 100 : '100%',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: isMobile ? 'translateY(-2px)' : 'translateX(5px)'
                  },
                  gap: isMobile ? 0 : 2,
                  width: '100%',
                  py: isMobile ? 0 : 1
                }}
              >
              <IconButton
                sx={{
                  width: 48,
                  height: 48,
                  background: isSelected
                    ? 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
                    : 'rgba(139, 92, 246, 0.1)',
                  '&:hover': {
                    background: isSelected
                      ? 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)'
                      : 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Icon sx={{ 
                  color: isSelected ? 'white' : 'text.secondary',
                  fontSize: 24
                }} />
              </IconButton>
              <Typography
                variant={isMobile ? "caption" : "body1"}
                sx={{
                  mt: isMobile ? 0.5 : 0,
                  color: isSelected ? 'primary.main' : 'text.secondary',
                  fontWeight: isSelected ? 600 : 400,
                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                  whiteSpace: 'nowrap'
                }}
              >
                {label}
              </Typography>
              </Box>
            </Tooltip>
          );
        })}
        {isMobile && (
          <>
            <Tooltip title="More options" placement="top">
              <Box
                onClick={handleMoreClick}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  flex: 1,
                }}
              >
                <IconButton
                  sx={{
                    width: 48,
                    height: 48,
                    background: moreAnchorEl ? 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)' : 'rgba(139, 92, 246, 0.1)',
                  }}
                >
                  <MoreVertIcon sx={{ color: moreAnchorEl ? 'white' : 'text.secondary' }} />
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: moreAnchorEl ? 'primary.main' : 'text.secondary',
                    fontWeight: moreAnchorEl ? 600 : 400,
                    fontSize: '0.8rem',
                  }}
                >
                  More
                </Typography>
              </Box>
            </Tooltip>

            <Menu
              anchorEl={moreAnchorEl}
              open={Boolean(moreAnchorEl)}
              onClose={handleMoreClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: -2,
                  background: 'linear-gradient(180deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
                  borderRadius: 2,
                  boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.1)',
                }
              }}
            >
              {moreNavItems.map(({ label, path, icon: Icon }) => {
                const isSelected = location.pathname === path;
                return (
                  <MenuItem
                    key={path}
                    onClick={() => handleMoreItemClick(path)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 1.5,
                      px: 2,
                      minWidth: 200,
                      borderRadius: 1,
                      '&:hover': {
                        background: 'rgba(139, 92, 246, 0.1)',
                      }
                    }}
                  >
                    <Icon sx={{ 
                      color: isSelected ? 'primary.main' : 'text.secondary',
                      fontSize: 24
                    }} />
                    <Typography
                      sx={{
                        color: isSelected ? 'primary.main' : 'text.secondary',
                        fontWeight: isSelected ? 600 : 400,
                      }}
                    >
                      {label}
                    </Typography>
                  </MenuItem>
                );
              })}
            </Menu>
          </>
        )}
      </Box>
  );

  return isMobile ? (
    <AppBar 
      position="fixed" 
      sx={{ 
        top: 'auto',
        bottom: 0,
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
        backdropFilter: 'blur(10px)',
        boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.05)',
        height: 90,
        borderTop: '1px solid rgba(139, 92, 246, 0.1)'
      }}
    >
      {renderNavItems()}
    </AppBar>
  ) : (
    <StyledDrawer
      variant="permanent"
      anchor="left"
    >
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        borderBottom: '1px solid rgba(139, 92, 246, 0.1)'
      }}>
        <IconButton
          sx={{
            width: 48,
            height: 48,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          }}
        >
          <DashboardIcon sx={{ color: 'white' }} />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          TikTok Trends
        </Typography>
      </Box>
      {renderNavItems()}
    </StyledDrawer>
  );
}

export default Navigation;
